import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import CampaignHistory from '@/components/CampaignHistory/CampaignHistory';
import CampaignSearchPopup from '@/components/CampaignSearchPopup/CampaignSearchPopup';
import CampaignStatus from '@/components/CampaignStatus/CampaignStatus';
import Help from '@/components/Help/Help';
import ScrollValidationIndicator from '@/components/ScrollValidationIndicator/ScrollValidationIndicator';
import CampaignHeaderSkeleton from '@/components/Skeletons/CampaignHeaderSkeleton';
import CampaignStatusSkeleton from '@/components/Skeletons/CampaignStatusSkeleton';
import SubCampaignHeader from '@/components/SubCampaignHeader/SubCampaignHeader';
import { mapAggregateModelSelections } from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/volumeDetails-utils';
import Summary from '@/containers/CampaignDetail/Summary/Summary';
import Attachments from '@/containers/SubCampaignDetail/Attachments/Attachments';
import Classification from '@/containers/SubCampaignDetail/Classification/Classification';
import Description from '@/containers/SubCampaignDetail/Description/Description';
import Feedback from '@/containers/SubCampaignDetail/Feedback/Feedback';
import Hardware from '@/containers/SubCampaignDetail/Hardware/Hardware';
import PlantFeedback from '@/containers/SubCampaignDetail/PlantFeedback/PlantFeedback';
import Recipients from '@/containers/SubCampaignDetail/Recipients/Recipients';
import Software from '@/containers/SubCampaignDetail/Software/Software';
import Vehicles from '@/containers/SubCampaignDetail/Vehicles/Vehicles';
import { CR_TYPE, errorTypes, helpTextTabs, stateTabs, statusSubCampaign, tabsCRSearchOrdered } from '@/helpers/constants';
import { getCRSearchFromCRInfo } from '@/helpers/opensearch-utils';
import { getCRidBySubCRid, hideFeedbackSubCR, scrollToField, tabLowerCase } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useError from '@/hooks/useError';
import useGoTo from '@/hooks/useGoTo';
import useSubCampaignValidate from '@/hooks/useSubCampaignValidate';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignInfo, setCampaignSearch, setCampaignVehicles, toggleCampaignHistory } from '@/redux/Campaign/campaign.action';
import { getCampaignHelp, getCampaignHistory, getCampaignSearch } from '@/redux/Campaign/campaign.reducer';
import { setHeaderLanguage } from '@/redux/Header/header.action';
import { resetSubCampaign, setSubCampaignComments, setSubCampaignInfo } from '@/redux/SubCampaign/subCampaign.action';
import { getFeedbackValidations, getSubcampaignComments, getSubCampaignInfo } from '@/redux/SubCampaign/subCampaign.reducer';
import { getHelpResizer, getSettingsInfo, getWFResizer } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import titleService from '@/services/titleService';
import { AffectedVehicles, CampaignInfo, CampaignSearch, HelpTextState, SubCRNotes, SubCampaignInfo, Validations, CampaignHistory as CampaignHistoryType } from '@/types';
import useQuery from '@/utils/hooks';
import styles from './SubCampaignDetail.module.scss';

interface RenderTabContentProps {
  tab: string;
  hideFeeback: boolean;
  setURLsNotLoaded: (urls: string[]) => void;
  urlsNotLoaded: string[];
}

const RenderTabContent: React.FC<RenderTabContentProps> = (props) => {
  const { tab, hideFeeback, setURLsNotLoaded, urlsNotLoaded } = props;

  // If feedback is hidden and current tab is feedback or plant-feedback, return null
  if (hideFeeback && ['feedback', 'plant-feedback'].includes(tab)) {
    return null;
  }

  const components: Record<string, React.ReactNode> = {
    attachments: <Attachments />,
    classification: <Classification />,
    'affected-hardware': <Hardware />,
    'affected-software': <Software />,
    'affected-vehicles': <Vehicles />,
    recipients: <Recipients />,
    feedback: (
      <Feedback
        setURLsNotLoaded={setURLsNotLoaded}
        urlsNotLoaded={urlsNotLoaded}
      />
    ),
    summary: <Summary />,
    'plant-feedback': <PlantFeedback />,
    description: <Description />,
  };

  return components[tab] ?? components['description'];
};

interface SubCampaignDetailProps {
  setComments: (notes: SubCRNotes[]) => void;
  setCRInfo: (crInfo: CampaignInfo | { id: string }, fullReset?: boolean) => void;
  setInfo: (subCRInfo: SubCampaignInfo) => void;
  setHeaderLang: (lang: string) => void;
  campaignHelp: HelpTextState;
  resizerWF: number;
  resizerHelp: number;
  subCampaignInfo: SubCampaignInfo;
  resetSubCR: () => void;
  setCRSearch: (search: CampaignSearch) => void;
  campaignSearch: CampaignSearch;
  validations: Validations;
  timeZone: string;
  setAffectedVehicles: (vehicles: AffectedVehicles) => void;
  campaignHistory: CampaignHistoryType;
  toggleHistory: (show: boolean) => void;
}

const SubCampaignDetail: React.FC<SubCampaignDetailProps> = (props) => {
  const { setComments, setCRInfo, setInfo, setHeaderLang, campaignHelp, resizerWF, resizerHelp, subCampaignInfo,
    resetSubCR, setCRSearch, campaignSearch, validations, timeZone, setAffectedVehicles, campaignHistory, toggleHistory } = props;
  const { id, tab, version } = useParams<{ id: string; tab: string; version: string }>();
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { handleCriticalError } = useError();
  const { addToast } = useToastContext();
  const [crLoaded, setCRLoaded] = useState(false);
  const { goTo } = useGoTo();
  const searchSubCRURL = useQuery().get('search');
  const isSummary = window.location.pathname.toLowerCase().includes('/summary');
  const [isValid, setIsValid] = useState(!isSummary);
  const isLoading = id !== subCampaignInfo?.id || !crLoaded || !isValid;
  const hideFeeback = hideFeedbackSubCR(subCampaignInfo);
  const readOnly = subCampaignInfo?.authorization?.readOnly;
  const validateSubCampaignForm = useSubCampaignValidate();
  const [urlsNotLoaded, setURLsNotLoaded] = useState<string[]>([]);

  const findTab = () => helpTextTabs.find(({ link }) => (link === tabLowerCase(tab)));

  const inlineStyles = {
    gridTemplateColumns: `${resizerWF}px auto`,
    ...(campaignHelp?.show ? { width: `calc(100% - ${resizerHelp}px)` } : {}),
  };

  const defineTab = (table: string) => (table === 'plant-feedback' ? 'summary_plant_feedback' : tabLowerCase(tab)?.replaceAll('-', '_'));

  const fetchCampaign = useApiRequest(campaignServices.getCampaign);
  const fetchSubCampaignInfo = useApiRequest(campaignServices.getSubCampaignInfo);
  const fetchSubCampaignComments = useApiRequest(campaignServices.getSubCampaignComments);

  const loadMainCampaignData = () => {
    fetchCampaign(getCRidBySubCRid(id), '', version)
      .then((info) => {
        setCRInfo(info, false);
        setCRLoaded(true);

        if (info?.type === CR_TYPE.CAR) {
          fetchSubCampaignComments(id, version)
            .then(setComments)
            .catch(handleCriticalError);
        }
      })
      .catch((err) => {
        if (err.name !== errorTypes.ABORT_ERROR) {
          setCRInfo({ id: id || '' }, false);
          // TODO: check this!!
          addToast(err.message, 'ERROR');

          if (err.status === 404) goTo('/dashboard', 2500);
        }
      });
  };

  const loadSubCRData = () => {
    fetchSubCampaignInfo(id)
      .then((info: SubCampaignInfo) => {
        setInfo(info);
        setHeaderLang(info?.plantLanguage);
      })
      .catch((err) => {
        if (err.name !== errorTypes.ABORT_ERROR) {
          addToast(err.message, 'ERROR');

          if (err.status === 404) goTo('/dashboard', 2500);
        }
      });

    if (searchSubCRURL) {
      (campaignServices.getHighligtsOnSearch(id, false, searchSubCRURL))
        .then((res) => {
          const subCrInfo = res;
          const crSearch: CampaignSearch = getCRSearchFromCRInfo(subCrInfo, searchSubCRURL);

          if (crSearch.fieldSelected?.tab !== stateTabs.feedback) {
            const targetPath = tabsCRSearchOrdered()[crSearch.fieldSelected?.tab ?? stateTabs.feedback];

            if (targetPath) {
              goTo({
                pathname: `/sub-campaign/${id}/${targetPath}`,
                search: window.location.search,
              });
            }
          }
          setCRSearch(crSearch);
        })
        .catch((err) => {
          if (err.name !== errorTypes.ABORT_ERROR) {
            addToast(err.message, 'ERROR');

            if (err.status === 404) {
              setCRSearch({
                ...campaignSearch,
                active: false,
              });
            }
          }
        });
    }
    loadMainCampaignData();
  };

  const refreshSubCRVehicles = () => {
    loadSubCRData();
    loadAffectedVehicles();
  };

  useEffect(() => {
    loadSubCRData();

    return () => {
      resetSubCR();
      toggleHistory(false);
    };
  }, [id, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAffectedVehicles = () => {
    Promise.all([
      campaignServices.getSubCampaign(id, 'AFFECTED_VEHICLES', version),
      campaignServices.getSubCampaign(id, 'LIMITATION_OF_VOLUME', version),
    ]).then((res) => {
      const [affVehicles, limitationVolumes] = res;
      const newState = {
        ...affVehicles.module,
        volumes: mapAggregateModelSelections([limitationVolumes.module], timeZone),
      };
      setAffectedVehicles(newState);
    }).catch(handleCriticalError);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (campaignSearch?.selectedCoincidenceFieldId && campaignSearch?.fieldSelected?.tab !== stateTabs.feedback) {
        await new Promise((resolve) => setInterval(() => { // wait until the field is rendered
          if (document.getElementById('subCampaignDetailContent')) resolve(1);
        }, 200));
        scrollToField('subCampaignDetailContent', campaignSearch?.selectedCoincidenceFieldId);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignSearch?.selectedCoincidenceFieldId]);

  const onLoadSummary = async () => {
    if (isSummary && !isLoading && !readOnly) {
      let isCampaingFormValid = false;
      setIsValid(false);
      isCampaingFormValid = await validateSubCampaignForm();

      if (!isCampaingFormValid) {
        goTo(`/sub-campaign/${id}/feedback`);
      }
    }
    setIsValid(true);
  };

  useEffect(() => {
    onLoadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCampaignInfo?.id]);

  useEffect(() => {
    if (subCampaignInfo?.status === statusSubCampaign.IN_ANALYSIS) {
      loadAffectedVehicles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCampaignInfo?.status]);

  useEffect(() => {
    titleService.updatePageTitle(t('titles.sub-campaign'), id);
  }, [t, id, lang]);

  return (
    <>
      <div className={campaignHelp?.show ? styles.BigContainerFlex : styles.BigContainer}>
        <div className={styles.CampaignDetail} style={inlineStyles}>
          {isLoading && <CampaignStatusSkeleton />}
          {!isLoading && <CampaignStatus isSubCR subCRStatus={subCampaignInfo?.status} />}
          <div className={styles.DetailContent}>
            {isLoading && <CampaignHeaderSkeleton isSummary={isSummary} />}
            {!isLoading && (
              <SubCampaignHeader
                id={id}
                tab={tabLowerCase(tab)}
                campaignHelp={campaignHelp}
                reloadSubCR={loadSubCRData}
                refreshSubCRVehicles={refreshSubCRVehicles}
                setURLsNotLoaded={setURLsNotLoaded}
              />
            )}
            <div className={styles.PageContent}>
              {crLoaded && (
                <>
                  {validations && <ScrollValidationIndicator containerId="subcampaign_detail_content" headerId="subcampaign_header" validations={validations} />}
                  {campaignHistory?.show && <CampaignHistory id={id || ''} reloadCR={loadSubCRData} currentTab={tab || ''} isSubCR />}
                  <div className={styles.TabContent} id="subcampaign_detail_content">
                    <RenderTabContent
                      tab={tab || 'description'}
                      hideFeeback={hideFeeback}
                      urlsNotLoaded={urlsNotLoaded}
                      setURLsNotLoaded={setURLsNotLoaded}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {campaignHelp?.show && findTab() && <Help tab={defineTab(tab || '')} />}
      </div>
      {campaignSearch?.active && <CampaignSearchPopup isSubCR />}
    </>
  );
};

const mapStateToProps = (state) => ({
  comments: getSubcampaignComments(state),
  campaignHelp: getCampaignHelp(state),
  resizerWF: getWFResizer(state),
  resizerHelp: getHelpResizer(state),
  subCampaignInfo: getSubCampaignInfo(state),
  campaignSearch: getCampaignSearch(state),
  validations: getFeedbackValidations(state),
  timeZone: getSettingsInfo(state)?.timezone,
  campaignHistory: getCampaignHistory(state),
});

const actionCreators = {
  setComments: setSubCampaignComments,
  setInfo: setSubCampaignInfo,
  setCRInfo: setCampaignInfo,
  setHeaderLang: setHeaderLanguage,
  resetSubCR: resetSubCampaign,
  setCRSearch: setCampaignSearch,
  setAffectedVehicles: setCampaignVehicles,
  toggleHistory: toggleCampaignHistory,
};

export default (connect(mapStateToProps, actionCreators)(SubCampaignDetail));
